/**
 * Web Vitals Performance Monitoring
 *
 * Integrates Core Web Vitals tracking using the web-vitals library
 * and sends metrics to Sentry for monitoring and alerting.
 *
 * Core Web Vitals measured:
 * - CLS (Cumulative Layout Shift): Visual stability
 * - LCP (Largest Contentful Paint): Loading performance
 * - TTFB (Time to First Byte): Server response time
 * - INP (Interaction to Next Paint): Responsiveness
 *
 * All metrics are sent to Sentry as custom metrics with rating tags
 * (good, needs-improvement, poor) for dashboard visualization.
 *
 * @format
 */

import * as Sentry from "@sentry/react";
import { onCLS, onINP, onLCP, onTTFB } from "web-vitals";

/**
 * Initialize Web Vitals performance monitoring.
 *
 * This should be called once during app initialization, preferably
 * in the main App component's useEffect.
 *
 * Only runs in production builds (not in development mode).
 */
export function initializePerformanceMonitoring(): void {
  if (import.meta.env.DEV) {
    return;
  }

  // Track Cumulative Layout Shift (CLS)
  // Measures visual stability - how much content shifts unexpectedly
  // Good: < 0.1, Needs improvement: 0.1-0.25, Poor: > 0.25
  onCLS(metric => {
    Sentry.setMeasurement("cls", metric.value, "none");
    Sentry.setTag("web_vital_cls_rating", metric.rating);
  });

  // Track Largest Contentful Paint (LCP)
  // Measures loading performance - when largest content element renders
  // Good: < 2.5s, Needs improvement: 2.5-4s, Poor: > 4s
  onLCP(metric => {
    Sentry.setMeasurement("lcp", metric.value, "millisecond");
    Sentry.setTag("web_vital_lcp_rating", metric.rating);
  });

  // Track Time to First Byte (TTFB)
  // Measures server response time
  // Good: < 800ms, Needs improvement: 800-1800ms, Poor: > 1800ms
  onTTFB(metric => {
    Sentry.setMeasurement("ttfb", metric.value, "millisecond");
    Sentry.setTag("web_vital_ttfb_rating", metric.rating);
  });

  // Track Interaction to Next Paint (INP)
  // Measures overall responsiveness to user interactions
  // Good: < 200ms, Needs improvement: 200-500ms, Poor: > 500ms
  onINP(metric => {
    Sentry.setMeasurement("inp", metric.value, "millisecond");
    Sentry.setTag("web_vital_inp_rating", metric.rating);
  });
}
